import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  CalendarX,
  Plus,
  Trash,
  PencilSimple,
  Warning,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
  selectHasPermission,
} from "../../../store/slices/authSlice";
import {
  useGetAbsencesQuery,
  useCreateAbsenceMutation,
  useUpdateAbsenceMutation,
  useDeleteAbsenceMutation,
  useLazyGetAppointmentsQuery,
} from "../../../store/services/calendarApi";
import { useToast } from "../../../contexts/ToastContext";
import DatePicker from "../../ui/DatePicker";
import TimePicker from "../../ui/TimePicker";
import {
  ABSENCE_TYPES,
  ABSENCE_TYPE_LABELS,
  ABSENCE_NOTE_MAX,
  toLocalDateStr,
  validateAbsenceInput,
  formatAbsenceRange,
  absenceBlocksSlot,
} from "../../../utils/absenceUtils";
import LoaderLoop from "../../shared/loader/LoaderLoop";
import ContentLoader from "../../shared/ContentLoader";
import "./AbsencesPanel.css";

const TYPE_OPTIONS = [
  { key: ABSENCE_TYPES.VACATION, label: ABSENCE_TYPE_LABELS.vacation },
  { key: ABSENCE_TYPES.SICK, label: ABSENCE_TYPE_LABELS.sick },
  { key: ABSENCE_TYPES.OTHER, label: ABSENCE_TYPE_LABELS.other },
];

const emptyForm = () => ({
  type: ABSENCE_TYPES.VACATION,
  startDate: "",
  endDate: "",
  allDay: true,
  startTime: "09:00",
  endTime: "13:00",
  note: "",
});

const fmtDateTime = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${d
    .toTimeString()
    .slice(0, 5)}`;
};

/**
 * Self-service absences (היעדרויות) for one therapist — no approval flow.
 * Creating one warns (never cancels) about existing appointments in range.
 * Admins get an in-app notification (sent by the firebase helper).
 *
 * Props: targetTherapistId / targetTherapistName default to the signed-in
 * therapist; admins (and `calendar.manageAbsences`) may manage a colleague's.
 */
function AbsencesPanel({ targetTherapistId, targetTherapistName }) {
  const clinicId = useSelector(selectClinicId);
  const myId = useSelector(selectTherapistId);
  const myName = useSelector(selectTherapistName);
  const isAdmin = useSelector(selectIsAdmin);
  const canManageOthers = useSelector(selectHasPermission("calendar.manageAbsences"));
  const { showSuccess, showError } = useToast();

  const therapistId = targetTherapistId || myId;
  const therapistName = targetTherapistId ? targetTherapistName : myName;
  const isSelf = therapistId === myId;
  const canManage = isSelf || isAdmin || canManageOthers;

  const today = toLocalDateStr(new Date());
  const range = useMemo(() => {
    const from = new Date();
    from.setFullYear(from.getFullYear() - 1);
    const to = new Date();
    to.setFullYear(to.getFullYear() + 2);
    return { from: toLocalDateStr(from), to: toLocalDateStr(to) };
  }, []);

  const { data: absences = [], isLoading } = useGetAbsencesQuery(
    { clinicId, therapistId, from: range.from, to: range.to },
    { skip: !clinicId || !therapistId },
  );
  const [createAbsence, { isLoading: isCreatingNew }] = useCreateAbsenceMutation();
  const [updateAbsence, { isLoading: isUpdating }] = useUpdateAbsenceMutation();
  const [deleteAbsence] = useDeleteAbsenceMutation();
  const [fetchAppointments, { isFetching: isCheckingAppointments }] =
    useLazyGetAppointmentsQuery();
  const isCreating = isCreatingNew || isUpdating;

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // absence being edited (null = new)
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [overlapWarning, setOverlapWarning] = useState(null); // { count, items }
  const [showPast, setShowPast] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const upcoming = useMemo(
    () => absences.filter((a) => a.endDate >= today).sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [absences, today],
  );
  const past = useMemo(
    () => absences.filter((a) => a.endDate < today).sort((a, b) => b.startDate.localeCompare(a.startDate)),
    [absences, today],
  );

  const setField = (name, value) => {
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === "startDate" && (!f.endDate || f.endDate < value)) next.endDate = value;
      return next;
    });
    setErrors((e) => ({ ...e, [name]: undefined }));
    setOverlapWarning(null);
  };

  const resetForm = () => {
    setForm(emptyForm());
    setErrors({});
    setOverlapWarning(null);
    setShowForm(false);
    setEditingId(null);
  };

  // Edit in place (one "updated" notification) instead of delete + recreate.
  const startEdit = (a) => {
    setForm({
      type: a.type || ABSENCE_TYPES.OTHER,
      startDate: a.startDate || "",
      endDate: a.endDate || a.startDate || "",
      allDay: a.allDay !== false,
      startTime: a.startTime || "09:00",
      endTime: a.endTime || "13:00",
      note: a.note || "",
    });
    setErrors({});
    setOverlapWarning(null);
    setConfirmDeleteId(null);
    setEditingId(a.id);
    setShowForm(true);
  };

  const doCreate = async () => {
    const absence = { therapistId, therapistName, ...form, note: form.note.trim() || null };
    try {
      if (editingId) {
        await updateAbsence({
          clinicId,
          absenceId: editingId,
          absence,
          actorId: myId,
          actorName: myName,
        }).unwrap();
        showSuccess("ההיעדרות עודכנה");
      } else {
        await createAbsence({
          clinicId,
          absence,
          actorId: myId,
          actorName: myName,
        }).unwrap();
        showSuccess("ההיעדרות נוספה");
      }
      resetForm();
    } catch (err) {
      console.error(editingId ? "[CK-3825] Error updating absence:" : "[CK-3821] Error creating absence:", {
        errorCode: editingId ? "CK-3825" : "CK-3821",
        errorMessage: err?.message,
        clinicId,
        absenceId: editingId || null,
        timestamp: new Date().toISOString(),
      });
      showError(editingId ? "שגיאה בעדכון ההיעדרות" : "שגיאה בהוספת ההיעדרות");
    }
  };

  const handleSubmit = async () => {
    const v = validateAbsenceInput(form);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    // Warn (never cancel) about existing appointments inside the absence.
    try {
      const start = new Date(`${form.startDate}T00:00:00`);
      const end = new Date(`${form.endDate}T23:59:59.999`);
      const appts = await fetchAppointments({
        clinicId,
        therapistId,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      }).unwrap();
      const hits = (appts || []).filter((apt) => {
        if (apt.status === "cancelled" || apt.isBlockedTime === true) return false;
        const d = new Date(apt.startTime);
        const dateStr = toLocalDateStr(d);
        const hhmm = d.toTimeString().slice(0, 5);
        const dur = Math.max(1, Math.round((new Date(apt.endTime) - d) / 60000));
        return absenceBlocksSlot(form, dateStr, hhmm, dur);
      });
      if (hits.length > 0) {
        setOverlapWarning({ count: hits.length, items: hits.slice(0, 5) });
        return;
      }
    } catch (err) {
      // Reading a colleague's appointments may be denied — proceed without the warning.
      console.error("[CK-3822] Error checking appointments for absence:", {
        errorCode: "CK-3822",
        errorMessage: err?.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
    }
    await doCreate();
  };

  const handleDelete = async (absence) => {
    try {
      await deleteAbsence({
        clinicId,
        absenceId: absence.id,
        actorId: myId,
        actorName: myName,
        absence,
      }).unwrap();
      showSuccess("ההיעדרות נמחקה");
    } catch (err) {
      console.error("[CK-3823] Error deleting absence:", {
        errorCode: "CK-3823",
        errorMessage: err?.message,
        clinicId,
        absenceId: absence.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה במחיקת ההיעדרות");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const renderRow = (a, { isPast = false } = {}) => (
    <li key={a.id} className={`abs-row ${isPast ? "past" : ""}`}>
      <div className="abs-row-main">
        <span className={`abs-type abs-type-${a.type}`}>{ABSENCE_TYPE_LABELS[a.type] || "אחר"}</span>
        <span className="abs-range">{formatAbsenceRange(a)}</span>
        {a.allDay === false && a.startTime && (
          <span className="abs-time">
            {a.startTime}–{a.endTime}
          </span>
        )}
        {a.note && <span className="abs-note">{a.note}</span>}
      </div>
      {canManage && !isPast && (
        confirmDeleteId === a.id ? (
          <div className="abs-confirm">
            <span>למחוק?</span>
            <button type="button" className="abs-btn-danger" onClick={() => handleDelete(a)}>
              מחיקה
            </button>
            <button type="button" className="abs-btn-ghost" onClick={() => setConfirmDeleteId(null)}>
              ביטול
            </button>
          </div>
        ) : (
          <div className="abs-confirm">
            <button
              type="button"
              className="abs-icon-btn"
              title="עריכת היעדרות"
              aria-label="עריכת היעדרות"
              onClick={() => startEdit(a)}
            >
              <PencilSimple size={15} weight="bold" />
            </button>
            <button
              type="button"
              className="abs-icon-btn"
              title="מחיקת היעדרות"
              aria-label="מחיקת היעדרות"
              onClick={() => setConfirmDeleteId(a.id)}
            >
              <Trash size={15} weight="bold" />
            </button>
          </div>
        )
      )}
    </li>
  );

  return (
    <div className="cset-card abs-card">
      <div className="cset-card-header">
        <span className="cset-card-icon abs-card-icon">
          <CalendarX size={20} weight="fill" />
        </span>
        <div className="cset-card-heading">
          <h3>היעדרויות</h3>
          <p>
            {isSelf
              ? "חופשות, מחלה והיעדרויות אחרות. הצוות יראה \"לא זמין/ה\" ביומן, ומנהלי הקליניקה יקבלו עדכון. תורים קיימים אינם מבוטלים אוטומטית."
              : `היעדרויות של ${therapistName || "המטפל/ת"}. עמיתים רואים רק "לא זמין/ה"; הסוג וההערה גלויים למנהלים ולמטפל/ת.`}
          </p>
        </div>
      </div>

      {canManage && !showForm && (
        <div className="abs-toolbar">
          <button type="button" className="cset-btn-primary abs-add-btn" onClick={() => setShowForm(true)}>
            <Plus size={15} weight="bold" />
            הוספת היעדרות
          </button>
        </div>
      )}

      {canManage && showForm && (
        <div className="abs-form">
          {editingId && <span className="abs-label">עריכת היעדרות</span>}
          <div className="abs-field">
            <label className="abs-label">סוג</label>
            <div className="abs-seg" role="radiogroup" aria-label="סוג היעדרות">
              {TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={form.type === opt.key}
                  className={`abs-seg-opt ${form.type === opt.key ? "active" : ""}`}
                  onClick={() => setField("type", opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="abs-grid">
            <div className="abs-field">
              <label className="abs-label">מתאריך</label>
              <DatePicker
                name="startDate"
                value={form.startDate}
                onChange={(e) => setField("startDate", e.target.value)}
                error={!!errors.startDate}
              />
              {errors.startDate && <span className="abs-error">{errors.startDate}</span>}
            </div>
            <div className="abs-field">
              <label className="abs-label">עד תאריך</label>
              <DatePicker
                name="endDate"
                value={form.endDate}
                onChange={(e) => setField("endDate", e.target.value)}
                error={!!errors.endDate}
              />
              {errors.endDate && <span className="abs-error">{errors.endDate}</span>}
            </div>
          </div>

          <label className="abs-check">
            <input
              type="checkbox"
              checked={form.allDay}
              onChange={(e) => setField("allDay", e.target.checked)}
            />
            <span>כל היום</span>
          </label>

          {!form.allDay && (
            <div className="abs-grid">
              <div className="abs-field">
                <label className="abs-label">משעה</label>
                <TimePicker
                  name="startTime"
                  value={form.startTime}
                  onChange={(e) => setField("startTime", e.target.value)}
                  error={!!errors.startTime}
                />
                {errors.startTime && <span className="abs-error">{errors.startTime}</span>}
              </div>
              <div className="abs-field">
                <label className="abs-label">עד שעה</label>
                <TimePicker
                  name="endTime"
                  value={form.endTime}
                  onChange={(e) => setField("endTime", e.target.value)}
                  error={!!errors.endTime}
                />
                {errors.endTime && <span className="abs-error">{errors.endTime}</span>}
              </div>
            </div>
          )}

          <div className="abs-field">
            <label className="abs-label">הערה (אופציונלי)</label>
            <input
              type="text"
              className="cset-input"
              maxLength={ABSENCE_NOTE_MAX}
              value={form.note}
              onChange={(e) => setField("note", e.target.value)}
              placeholder="לדוגמה: כנס מקצועי"
            />
          </div>

          {overlapWarning ? (
            <div className="abs-warning" role="alert">
              <div className="abs-warning-title">
                <Warning size={16} weight="fill" />
                <span>יש {overlapWarning.count} תורים בתקופה זו</span>
              </div>
              <ul className="abs-warning-list">
                {overlapWarning.items.map((apt) => (
                  <li key={apt.id}>
                    {fmtDateTime(apt.startTime)}
                    {apt.patientName ? ` — ${apt.patientName}` : ""}
                  </li>
                ))}
                {overlapWarning.count > overlapWarning.items.length && (
                  <li>ועוד {overlapWarning.count - overlapWarning.items.length}…</li>
                )}
              </ul>
              <p className="abs-warning-note">
                התורים לא יבוטלו ולא תישלח הודעה למטופלים. ניתן להעביר אותם מהיומן.
              </p>
              <div className="abs-actions">
                <button type="button" className="cset-btn-ghost" onClick={() => setOverlapWarning(null)}>
                  חזרה
                </button>
                <button type="button" className="cset-btn-primary" onClick={doCreate} disabled={isCreating}>
                  {isCreating ? "שומר..." : editingId ? "עדכן/י בכל זאת" : "הוסף/י בכל זאת"}
                </button>
              </div>
            </div>
          ) : (
            <div className="abs-actions">
              <button type="button" className="cset-btn-ghost" onClick={resetForm}>
                ביטול
              </button>
              <button
                type="button"
                className="cset-btn-primary"
                onClick={handleSubmit}
                disabled={isCreating || isCheckingAppointments}
              >
                {isCreating || isCheckingAppointments ? (
                  <>
                    <LoaderLoop size="xs" /> בודק…
                  </>
                ) : editingId ? (
                  "שמירת שינויים"
                ) : (
                  "שמירת היעדרות"
                )}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="abs-list-wrap">
        {isLoading ? (
          <ContentLoader size="sm" compact message="טוען…" />
        ) : upcoming.length === 0 ? (
          <div className="abs-empty">אין היעדרויות קרובות</div>
        ) : (
          <ul className="abs-list">{upcoming.map((a) => renderRow(a))}</ul>
        )}

        {past.length > 0 && (
          <>
            <button type="button" className="abs-past-toggle" onClick={() => setShowPast((v) => !v)}>
              {showPast ? <CaretUp size={14} /> : <CaretDown size={14} />}
              {showPast ? "הסתרת היעדרויות קודמות" : `היעדרויות קודמות (${past.length})`}
            </button>
            {showPast && <ul className="abs-list">{past.map((a) => renderRow(a, { isPast: true }))}</ul>}
          </>
        )}
      </div>
    </div>
  );
}

export default AbsencesPanel;
